import * as qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import Alert, { EAlertColor } from '../../../components/UI/Alert/Alert';
import Button  from '../../../components/UI/Button/Button';
import  { EInputType, IInputField, IOption } from '../../../components/UI/Input/Input';
import { useInputs } from '../../../components/UI/Input/useInputs';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { IAppState, IFeedback, IFeedbackState } from '../../../interfaces';
import { createOptions } from '../../../shared/option-utils';

import * as actions from '../../../store/actions';
import { ETranslation } from '../../../translations/translation-keys';

interface IProps extends Partial<RouteComponentProps> {
  offerRequestId?: string;
  pinCode?: string;
  authAccessKey?: string;
  embedded?: boolean;
}

export enum EFeedback {
  serviceProvider = "serviceProvider",
  serviceProviderOther = "serviceProviderOther",
  serviceSatisfaction = "serviceSatisfaction",
  operatorToken = "operatorToken",
  serviceToken = "serviceToken",
  message = "message",
}

const Feedback: React.FC<IProps> = ({ history, location, offerRequestId, pinCode, authAccessKey, embedded }) => {
  const [id, setId] = useState("");
  const [pin, setPin] = useState("");
  const [accessKey, setAccessKey] = useState("");

  const [serviceProviderOptions, setServiceProviderOptions] = useState<
    IOption[]
  >([]);

  useEffect(() => {
    if (offerRequestId) {
      setId(offerRequestId);
      setPin(pinCode || "");
      setAccessKey(authAccessKey || "");
    } else if (location?.search) {
      const q = qs.parse(location.search);
      setId(q.id as string);
      setPin(q.pin as string);
      setAccessKey(q.accessKey as string);
    }
  }, [location, offerRequestId, pinCode, authAccessKey]);


  const { loading, serviceProviders, saveOk, error } = useSelector<IAppState, IFeedbackState>(state => state.feedbacks)

  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [inputs, setInputs] = useState<IInputField>({
    [EFeedback.serviceSatisfaction]: {
      type: EInputType.star,
      labelTranslation: ETranslation.FEEDBACK_SATISFACTION,
      placeholderTranslation: ETranslation.FEEDBACK_SATISFACTION,
      value: "",
      options: [
        { value: "1" },
        { value: "2" },
        { value: "3" },
        { value: "4" },
        { value: "5" },
      ],
    },

    [EFeedback.serviceProvider]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.FEEDBACK_SERVICEPROVIDER,
      placeholderTranslation: ETranslation.FEEDBACK_SERVICEPROVIDER,
      value: ETranslation.FEEDBACK_FORM_NOT_FOUND,
      options: [],
    },
    [EFeedback.serviceProviderOther]: {
      type: EInputType.text,
      labelTranslation: ETranslation.FEEDBACK_SERVICE_PROVIDER_OTHER,
      placeholderTranslation: ETranslation.FEEDBACK_SERVICE_PROVIDER_OTHER,
      value: "",
    },
    [EFeedback.serviceToken]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_TOKEN,
      value: "",
    },
    [EFeedback.message]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.FEEDBACK_MESSAGE,
      placeholderTranslation: ETranslation.FEEDBACK_MESSAGE,
      value: "",
    },
  });

  // get offer request
  useEffect(() => {
    if (id && (pin || accessKey)) {
      dispatch(actions.getFeedbackFormData(id, pin, accessKey));
    }
  }, [dispatch, id, pin, accessKey]);

  // put service providers into input
  useEffect(() => {
    if (serviceProviders) {
      const options = createOptions(serviceProviders, (s) => s.name || "");
      options.unshift({
        value: ETranslation.FEEDBACK_FORM_NOT_FOUND,
        label: t(ETranslation.FEEDBACK_FORM_NOT_FOUND),
      });
      options.push({
        value: ETranslation.FEEDBACK_FORM_SOMETHING_ELSE,
        label: t(ETranslation.FEEDBACK_FORM_SOMETHING_ELSE),
      });
      setServiceProviderOptions(options);
    }
  }, [serviceProviders, t]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
  });

  const submitHandler = async (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const data = await submit();
    if (!data) return;
    const feedback = data as IFeedback;

    const serviceProvider = inputs[EFeedback.serviceProvider].value as string;
    if (serviceProvider) {
      switch (serviceProvider) {
        case ETranslation.FEEDBACK_FORM_NOT_FOUND:
          feedback.isServiceProviderNotFound = true;
          feedback.serviceProvider = undefined;
          break;
        case ETranslation.FEEDBACK_FORM_SOMETHING_ELSE:
          feedback.isServiceProviderOther = true;
          feedback.serviceProvider = undefined;
          break;
      }
    }
    
    dispatch(actions.saveFeedback(feedback, id, pin, accessKey));
  };

  const serviceSatification = inputs[EFeedback.serviceSatisfaction]
    .value as string;
  const serviceProvider = inputs[EFeedback.serviceProvider].value as string;

  if (saveOk) {
    return (
      <Alert color={EAlertColor.SUCCESS}>
        {t(ETranslation.FEEDBACK_THANK_YOU_MESSAGE)}
      </Alert>
    )
  }

  if (error) {
    return <Alert>{t(error)}</Alert>
  }

  return (
    <div className={embedded ? "" : "Container"}>
      {!embedded && <h2>{t(ETranslation.TITLE_FEEDBACKS)}</h2>}
      {loading ? (
        <Spinner />
      ) : (
        <form onSubmit={submitHandler}>
          {createInput(EFeedback.serviceSatisfaction)}
          {inputs[EFeedback.serviceSatisfaction].value && (
            <>
              {createInput(EFeedback.message)}
              {createInput(EFeedback.serviceProvider, {
                options: serviceProviderOptions,
                loading,
              })}
              {serviceProvider ===
                ETranslation.FEEDBACK_FORM_SOMETHING_ELSE &&
                createInput(EFeedback.serviceProviderOther)}
            </>
          )}
        </form>
      )}
      <div>
        <Button
          type="submit"
          disabled={loading || !serviceSatification}
          onClick={submitHandler}
        >
          {t(ETranslation.COMMON_SEND)}
        </Button>
      </div>
    </div>
  );
};

export default Feedback;
